import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Zap } from "lucide-react";
import FlashBadge from "@/components/FlashBadge";

/**
 * FlashBadgeButton — a big obvious CTA that fetches the crew's badge token
 * (once, cached) and opens the FlashBadge full-screen overlay when tapped.
 *
 * Variants:
 *   • "inline"  — full-width prominent bar (default, sits inline on desktop)
 *   • "compact" — pill button (fits nicely in a toolbar)
 *   • "fab"     — floating action button, bottom-right on mobile
 *
 * Reuses one token across the app so the SPA only hits /api/id/mine/enable
 * a single time per session.
 */
export default function FlashBadgeButton({ variant = "inline", label = "FLASH BADGE", testId }) {
  const [token, setToken] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let ok = true;
    // Silent probe — if the user hasn't purchased Digital ID yet, we DON'T
    // want to pop the global upgrade modal every time the button mounts.
    api.post("/id/mine/enable", null, { silent402: true })
      .then((r) => { if (ok) setToken(r.data.public_token); })
      .catch(() => {});
    return () => { ok = false; };
  }, []);

  const openFlash = async () => {
    if (busy) return;
    setBusy(true);
    // If token wasn't pre-fetched yet (network was slow), fetch on-demand.
    // The click IS a user intent so 402 SHOULD pop the modal here — no silent.
    let t = token;
    if (!t) {
      try {
        const r = await api.post("/id/mine/enable");
        t = r.data.public_token;
        setToken(t);
      } catch { return setBusy(false); }
    }
    setBusy(false);
    if (t) setOpen(true);
  };

  const shared = "font-bold tracking-widest uppercase transition-all";

  let el;
  if (variant === "fab") {
    el = (
      <button
        type="button"
        data-testid={testId || "flash-badge-fab"}
        onClick={openFlash}
        aria-label="Flash my PJP Digital ID"
        className={`${shared} lg:hidden fixed z-40 bottom-16 right-4 h-14 w-14 rounded-full bg-cyan text-obsidian shadow-2xl hover:scale-105 active:scale-95 grid place-items-center border-2 border-white/20`}
        style={{ boxShadow: "0 8px 32px rgba(75, 156, 255, 0.55), 0 0 0 6px rgba(75,156,255,0.15)" }}
      >
        <Zap className="h-6 w-6" strokeWidth={2.5} />
      </button>
    );
  } else if (variant === "compact") {
    el = (
      <button
        type="button"
        data-testid={testId || "flash-badge-compact"}
        onClick={openFlash}
        className={`${shared} mono text-[10px] tracking-widest px-3 py-1.5 rounded border border-cyan/50 bg-cyan/10 text-cyan hover:bg-cyan/20 flex items-center gap-1.5`}
      >
        <Zap className="h-3 w-3" /> {label}
      </button>
    );
  } else {
    el = (
      <button
        type="button"
        data-testid={testId || "flash-badge-inline"}
        onClick={openFlash}
        className={`${shared} w-full md:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-cyan text-obsidian text-sm hover:brightness-110 hover:scale-[1.02] active:scale-100`}
        style={{ boxShadow: "0 0 24px rgba(75, 156, 255, 0.35), inset 0 -2px 0 rgba(0,0,0,0.15)" }}
      >
        <Zap className="h-4 w-4" strokeWidth={2.5} /> {label}
        <span className="mono text-[9px] tracking-widest opacity-70 ml-1">/ SHOW TO FBO</span>
      </button>
    );
  }

  return (
    <>
      {el}
      <FlashBadge open={open} token={token} onClose={() => setOpen(false)} />
    </>
  );
}
